<template lang='pug'>
  //- Row action icon button with tooltip (icon-only, label shown as tooltip and aria-label)
  mixin actBtn(labelExpr, clickExpr, clsExpr, disabledExpr)
    v-tooltip(bottom)
      template(v-slot:activator='{ on, attrs }')
        button.icon-btn(type='button' :class=clsExpr v-bind='attrs' v-on='on' :aria-label=labelExpr :disabled=disabledExpr @click=clickExpr)
          block
      span(v-text=labelExpr)
  //- Code block with label bar and copy button
  mixin codeBlock(textExpr, labelExpr, copyId)
    .code-block
      .code-bar
        span(v-text=labelExpr)
        button.copy-btn(type='button' :class=`{ done: copiedId === '${copyId}' }` @click=`copyText(${textExpr}, '${copyId}')`)
          v-icon(size='14') mdi-content-copy
          span(v-text=`copiedId === '${copyId}' ? $t('INTEGRATION.COMMON.COPIED') : $t('INTEGRATION.COMMON.COPY')`)
      pre(v-text=textExpr)
  #Integrations.main-page.GPT-color.page-noScroll.pb-0
    v-row#page-head.int-head.d-flex.align-center
      v-col.px-0.pb-0.px-md-2.pb-md-2(cols='12')
        h1.my-0(style='font-weight: 700; font-size: 28px;') {{$t('INTEGRATION.TITLE')}}
        p.page-sub.mt-1.mb-0 {{$t('INTEGRATION.SUBTITLE')}}
    .int-inner
      .dpanel
        .dbar
          v-tabs.int-seg(v-model='tab' @change='onTabChange' hide-slider height='32' background-color='transparent')
            v-tab(href='#api-keys' :ripple='false') {{$t('INTEGRATION.TAB.API_KEYS')}}({{keys.length}})
            v-tab(href='#call-logs' :ripple='false') {{$t('INTEGRATION.TAB.CALL_LOGS')}}{{logTotal === null ? '' : '(' + logTotal + ')'}}
            v-tab(href='#how-to' :ripple='false') {{$t('INTEGRATION.TAB.HOW_TO')}}
          .dbar-right(v-if='tab === "api-keys" && keys.length')
            v-btn.int-btn-primary(depressed @click='openCreate')
              svg.plus-ic(width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2.2' stroke-linecap='round' aria-hidden='true')
                path(d='M12 4v16M4 12h16')
              | {{$t('INTEGRATION.KEY.CREATE')}}
        v-tabs-items(v-model='tab')
          v-tab-item(value='api-keys' transition='none')
            .integration-api-keys
              v-progress-linear(v-if='loading' indeterminate color='#32c7dc')
              .empty(v-else-if='!keys.length')
                .ill
                  v-icon(size='36' color='#1fb3d1') mdi-key-variant
                p {{$t('INTEGRATION.KEY.EMPTY')}}
                  br
                  | {{$t('INTEGRATION.KEY.EMPTY_DESC')}}
                v-btn.int-btn-primary(depressed @click='openCreate')
                  svg.plus-ic(width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2.2' stroke-linecap='round' aria-hidden='true')
                    path(d='M12 4v16M4 12h16')
                  | {{$t('INTEGRATION.KEY.CREATE')}}
              .dwrap(v-else)
                table.dtable
                  thead
                    tr
                      th {{$t('INTEGRATION.KEY.NAME')}}
                      th {{$t('INTEGRATION.KEY.COL_KEY')}}
                      th {{$t('INTEGRATION.KEY.COL_ALLOWED_APIS')}}
                      th {{$t('INTEGRATION.KEY.COL_STATUS')}}
                      th {{$t('INTEGRATION.KEY.COL_CREATED')}}
                      th.integration-actions {{$t('INTEGRATION.KEY.COL_ACTIONS')}}
                  tbody
                    tr(v-for='k in keys' :key='k.id' :class='{ muted: !k.is_active }')
                      td
                        .cell-name(:title='k.name') {{k.name}}
                      td.mono {{k.masked_key}}
                      td {{k.allowed_api_count}}
                      td
                        span.badge(:class='k.is_active ? "badge--green" : "badge--grey"')
                          span.dot
                          | {{k.is_active ? $t('INTEGRATION.KEY.ENABLED') : $t('INTEGRATION.KEY.DISABLED')}}
                      td.nowrap {{formatDate(k.created_on)}}
                      td.integration-actions
                        .ex
                          +actBtn("$t('INTEGRATION.KEY.SET_ALLOWED_APIS')", "openAllowed(k)", "''", "busyId === k.id")
                            svg(width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round' aria-hidden='true')
                              path(d='M4 6l1.5 1.5L8 5M4 12l1.5 1.5L8 11M4 18l1.5 1.5L8 17M11 6h9M11 12h9M11 18h9')
                          +actBtn("$t('INTEGRATION.KEY.VIEW_CALL_LOGS')", "viewCallLogs(k)", "''", "false")
                            svg(width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round' aria-hidden='true')
                              path(d='M3 12a9 9 0 1 0 3-6.7L3 8')
                              path(d='M3 3v5h5M12 7v5l3 2')
                          +actBtn("k.is_active ? $t('INTEGRATION.KEY.DISABLE') : $t('INTEGRATION.KEY.ENABLE')", "toggleActive(k)", "''", "busyId === k.id")
                            svg(v-if='k.is_active' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round' aria-hidden='true')
                              circle(cx='12' cy='12' r='8.5')
                              path(d='M6 6l12 12')
                            svg(v-else width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round' aria-hidden='true')
                              circle(cx='12' cy='12' r='8.5')
                              path(d='M8 12.5l2.5 2.5L16 9.5')
                          +actBtn("$t('INTEGRATION.KEY.DELETE')", "openDelete(k)", "'danger'", "busyId === k.id")
                            svg(width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='1.7' stroke-linecap='round' stroke-linejoin='round' aria-hidden='true')
                              path(d='M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v6M14 11v6')
          v-tab-item(value='call-logs' transition='none')
            .integration-call-logs
              .dfilters(role='search')
                .f
                  label.ds-label {{$t('INTEGRATION.LOG.COL_KEY')}}
                  v-select.log-filter(v-model='logFilter.api_key' :items='keyOptions' item-text='text' item-value='value' :aria-label='$t("INTEGRATION.LOG.COL_KEY")' outlined dense hide-details @change='onFilterChange')
                .f
                  label.ds-label {{$t('INTEGRATION.LOG.COL_API')}}
                  v-select.log-filter(v-model='logFilter.api_code' :items='apiOptions' item-text='text' item-value='value' :aria-label='$t("INTEGRATION.LOG.COL_API")' outlined dense hide-details @change='onFilterChange')
                .f
                  label.ds-label {{$t('INTEGRATION.LOG.FILTER_RESULT')}}
                  v-select.log-filter(v-model='logFilter.result' :items='resultOptions' item-text='text' item-value='value' :aria-label='$t("INTEGRATION.LOG.FILTER_RESULT")' outlined dense hide-details @change='onFilterChange')
                .f
                  label.ds-label {{$t('INTEGRATION.LOG.FILTER_FROM')}}
                  v-text-field.log-filter(v-model='logFilter.start' type='date' :aria-label='$t("INTEGRATION.LOG.FILTER_FROM")' outlined dense hide-details @change='onFilterChange')
                .f
                  label.ds-label {{$t('INTEGRATION.LOG.FILTER_TO')}}
                  v-text-field.log-filter(v-model='logFilter.end' type='date' :aria-label='$t("INTEGRATION.LOG.FILTER_TO")' outlined dense hide-details @change='onFilterChange')
                v-btn.int-btn-secondary.int-btn-sm(v-if='hasLogFilter' outlined small @click='clearLogFilter') {{$t('INTEGRATION.LOG.FILTER_CLEAR')}}
              v-progress-linear(v-if='logsLoading' indeterminate color='#32c7dc')
              .empty(v-else-if='!logs.length')
                .ill
                  v-icon(size='36' color='#1fb3d1') mdi-text-box-outline
                p {{hasLogFilter ? $t('INTEGRATION.LOG.EMPTY_FILTERED') : $t('INTEGRATION.LOG.EMPTY')}}
                v-btn.int-btn-secondary(v-if='hasLogFilter' outlined @click='clearLogFilter') {{$t('INTEGRATION.LOG.FILTER_CLEAR')}}
              template(v-else)
                .dwrap
                  table.dtable
                    thead
                      tr
                        th {{$t('INTEGRATION.LOG.COL_TIME')}}
                        th {{$t('INTEGRATION.LOG.COL_KEY')}}
                        th {{$t('INTEGRATION.LOG.COL_API')}}
                        th {{$t('INTEGRATION.LOG.COL_HTTP_STATUS')}}
                    tbody
                      tr.clickable(v-for='l in logs' :key='l.id' tabindex='0' @click='openLogDetail(l)' @keyup.enter='openLogDetail(l)')
                        td.nowrap {{formatDate(l.called_at)}}
                        td
                          div {{l.api_key_name}}
                          .sub.mono {{l.masked_key}}
                        td {{apiName(l.api_code)}}
                        td
                          span.badge(:class='isSuccess(l.http_status) ? "badge--green" : "badge--red"') {{l.http_status}} {{isSuccess(l.http_status) ? $t('INTEGRATION.LOG.SUCCESS') : $t('INTEGRATION.LOG.FAILED')}}
                .dpager
                  span.total {{$t('INTEGRATION.LOG.TOTAL', { count: logCount })}}
                  v-pagination(v-if='logPageCount > 1' v-model='logPage' :length='logPageCount' :total-visible='7' color='#4a8cf0' @input='loadLogs')
          v-tab-item(value='how-to' transition='none')
            .integration-how-to.steps
              .step
                .step-no 1
                div
                  h3 {{$t('INTEGRATION.HOW_TO.STEP1_TITLE')}}
                  p {{$t('INTEGRATION.HOW_TO.STEP1_DESC')}}
                  a.how-to-link(href='#' @click.prevent='goTab("api-keys")') {{$t('INTEGRATION.HOW_TO.GO_TO_API_KEYS')}}
              .step
                .step-no 2
                div
                  h3 {{$t('INTEGRATION.HOW_TO.STEP2_TITLE')}}
                  p {{$t('INTEGRATION.HOW_TO.STEP2_DESC')}}
                  a.how-to-link(href='#' @click.prevent='goTab("api-keys")') {{$t('INTEGRATION.HOW_TO.GO_TO_API_KEYS')}}
              .step
                .step-no 3
                .step-main
                  h3 {{$t('INTEGRATION.HOW_TO.STEP3_TITLE')}}
                  p {{$t('INTEGRATION.HOW_TO.STEP3_DESC')}}
                  +codeBlock("headerLine", "$t('INTEGRATION.REF.HEADERS')", "how-to-header")
                  .note {{$t('INTEGRATION.HOW_TO.STEP3_NOTE')}}
    v-dialog(v-model='createDialog' max-width='480' persistent content-class='int-dialog-wrap' overlay-color='#000' overlay-opacity='0.4')
      v-card.int-dialog
        .int-dhead
          h2 {{$t('INTEGRATION.KEY.CREATE')}}
        .int-dbody
          .field
            .field-label-row
              label.field-label(for='int-key-name') {{$t('INTEGRATION.KEY.NAME')}}
                span.req *
              span.field-counter {{createNameLength}}/{{NAME_MAX_LENGTH}}
            v-text-field(id='int-key-name' ref='nameField' v-model='createName' :placeholder='$t("INTEGRATION.KEY.NAME_PLACEHOLDER")' :hint='$t("INTEGRATION.KEY.NAME_HINT")' persistent-hint :error-messages='nameError' outlined dense autofocus @keyup.enter='submitCreate' @input='onNameInput' @blur='onNameBlur')
        .int-dactions
          v-btn.int-btn-secondary(outlined :disabled='submitting' @click='createDialog = false') {{$t('INTEGRATION.COMMON.CANCEL')}}
          v-btn.int-btn-primary(depressed :loading='submitting' @click='submitCreate') {{$t('INTEGRATION.KEY.CREATE')}}
    v-dialog(v-model='keyDialog' max-width='720' persistent content-class='int-dialog-wrap' overlay-color='#000' overlay-opacity='0.4')
      v-card.int-dialog
        .int-dhead
          h2 {{$t('INTEGRATION.KEY.CREATED_TITLE')}}
        .int-dbody
          div {{$t('INTEGRATION.KEY.CREATED_FULL_KEY', { name: createdKey.name })}}
          .key-box
            code {{createdKey.key}}
            button.copy-btn(type='button' :class='{ done: copied }' @click='copyKey')
              v-icon(size='14') mdi-content-copy
              span {{copied ? $t('INTEGRATION.COMMON.COPIED') : $t('INTEGRATION.COMMON.COPY')}}
          .callout.callout--warn
            v-icon(size='18' color='#8a5a00') mdi-alert-outline
            span
              strong {{$t('INTEGRATION.KEY.CREATED_WARNING')}}
              br
              | {{$t('INTEGRATION.KEY.CREATED_LOST_HINT')}}
          .callout.callout--info
            v-icon(size='18' color='#20606b') mdi-information-outline
            span {{$t('INTEGRATION.KEY.CREATED_NEXT_STEP')}}
        .int-dactions
          v-btn.int-btn-primary(depressed @click='closeKeyDialog') {{$t('INTEGRATION.KEY.SAVED_CLOSE')}}
    v-dialog(v-model='deleteDialog' max-width='480' persistent content-class='int-dialog-wrap' overlay-color='#000' overlay-opacity='0.4')
      v-card.int-dialog
        .int-dhead
          h2(:title='$t("INTEGRATION.KEY.DELETE_TITLE", { name: deleteTarget.name })') {{$t('INTEGRATION.KEY.DELETE_TITLE', { name: deleteTarget.name })}}
        .int-dbody {{$t('INTEGRATION.KEY.DELETE_DESC')}}
        .int-dactions
          v-btn.int-btn-secondary(outlined :disabled='deleting' @click='deleteDialog = false') {{$t('INTEGRATION.COMMON.CANCEL')}}
          v-btn.int-btn-danger(depressed :loading='deleting' @click='confirmDelete') {{$t('INTEGRATION.KEY.DELETE')}}
    v-dialog(v-model='allowedDialog' max-width='600' persistent scrollable content-class='int-drawer' transition='slide-x-reverse-transition' overlay-color='#000' overlay-opacity='0.1')
      v-card.int-drawer-card(v-if='apiReferenceCode')
        .int-drawer-head
          h2 {{$t('INTEGRATION.ALLOWED.USAGE_TITLE', { name: apiName(apiReferenceCode) })}}
          button.icon-btn(type='button' :aria-label='$t("INTEGRATION.REF.BACK")' @click='backToApiList')
            v-icon(size='20') mdi-close
        .int-drawer-body
          button.btn-link.back-link(type='button' @click='backToApiList')
            v-icon(size='20' color='#14C6DE') mdi-chevron-left
            | {{$t('INTEGRATION.REF.BACK')}}
          p.mb-2.mt-3 {{$t('INTEGRATION.API.' + apiReferenceCode + '.DESCRIPTION')}}
          .doc-h.first {{$t('INTEGRATION.REF.METHOD_AND_URL')}}
          .inline-code
            span.method {{apiRef.method}}
            code {{apiRef.url}}
            button.copy-btn(type='button' :class='{ done: copiedId === "url" }' @click='copyText(apiRef.url, "url")')
              v-icon(size='14') mdi-content-copy
              span {{copiedId === 'url' ? $t('INTEGRATION.COMMON.COPIED') : $t('INTEGRATION.COMMON.COPY')}}
          .doc-h {{$t('INTEGRATION.REF.HEADERS')}}
          table.mini-table
            thead
              tr
                th {{$t('INTEGRATION.REF.COL_FIELD')}}
                th {{$t('INTEGRATION.REF.COL_VALUE')}}
                th {{$t('INTEGRATION.REF.COL_REQUIRED')}}
            tbody
              tr
                td
                  span.inline-code
                    code {{headerName}}
                    button.copy-btn(type='button' :class='{ done: copiedId === "header" }' @click='copyText(headerLine, "header")')
                      v-icon(size='14') mdi-content-copy
                      span {{copiedId === 'header' ? $t('INTEGRATION.COMMON.COPIED') : $t('INTEGRATION.COMMON.COPY')}}
                td.mono {{apiKeyPlaceholder}}
                td {{$t('INTEGRATION.REF.YES')}}
              tr
                td.mono Content-Type
                td.mono application/json
                td {{$t('INTEGRATION.REF.YES')}}
          .helper.mt-2 {{$t('INTEGRATION.REF.HEADER_DESC')}}
          .helper {{$t('INTEGRATION.REF.PLACEHOLDER_NOTE')}}
          .doc-h {{$t('INTEGRATION.REF.REQUEST_BODY')}}
          table.mini-table
            thead
              tr
                th {{$t('INTEGRATION.REF.COL_FIELD')}}
                th {{$t('INTEGRATION.REF.COL_TYPE')}}
                th {{$t('INTEGRATION.REF.COL_REQUIRED')}}
                th {{$t('INTEGRATION.REF.COL_DESCRIPTION')}}
            tbody
              tr(v-for='f in requestFields' :key='f.name')
                td.mono {{f.name}}
                td {{f.type}}
                td {{$t('INTEGRATION.REF.YES')}}
                td {{$t('INTEGRATION.REF.FIELD.' + f.name)}}
          +codeBlock("requestExample", "$t('INTEGRATION.REF.REQUEST_BODY_EXAMPLE')", "request")
          +codeBlock("curlExample", "$t('INTEGRATION.REF.CURL_EXAMPLE')", "curl")
          .doc-h {{$t('INTEGRATION.REF.SUCCESS_RESPONSE')}}
          table.mini-table
            thead
              tr
                th {{$t('INTEGRATION.REF.COL_FIELD')}}
                th {{$t('INTEGRATION.REF.COL_TYPE')}}
                th {{$t('INTEGRATION.REF.COL_DESCRIPTION')}}
            tbody
              tr(v-for='f in responseFields' :key='f.name')
                td.mono
                  span(:class='{"ref-indent": f.indent}') {{f.name}}
                td {{f.type}}
                td {{$t('INTEGRATION.REF.FIELD.' + f.name)}}
          +codeBlock("responseExample", "$t('INTEGRATION.REF.RESPONSE_EXAMPLE')", "response")
          .doc-h {{$t('INTEGRATION.REF.ERRORS')}}
          p.helper.mb-1 {{$t('INTEGRATION.REF.ERROR_FORMAT')}}
          table.mini-table
            thead
              tr
                th {{$t('INTEGRATION.REF.HTTP_STATUS')}}
                th {{$t('INTEGRATION.REF.ERROR_CODE')}}
                th {{$t('INTEGRATION.REF.REASON')}}
            tbody
              tr(v-for='e in errorRows' :key='e.code')
                td {{e.status}}
                td.mono {{e.code}}
                td {{$t('INTEGRATION.REF.ERROR_REASON.' + e.code)}}
          +codeBlock("errorExample", "$t('INTEGRATION.REF.ERROR_EXAMPLE')", "error")
          .doc-h {{$t('INTEGRATION.REF.AVAILABLE_AGENTS')}}
          v-progress-linear(v-if='agentsLoading' indeterminate color='#32c7dc')
          p.helper(v-else-if='agentsLoaded && !agents.length') {{$t('INTEGRATION.REF.NO_AGENTS')}}
          table.mini-table(v-else-if='agents.length')
            thead
              tr
                th {{$t('INTEGRATION.REF.AGENT_NAME')}}
                th {{$t('INTEGRATION.REF.AGENT_ID')}}
            tbody
              tr(v-for='ag in agents' :key='ag.id')
                td {{ag.agent_name}}
                td
                  span.inline-code
                    code {{ag.id}}
                    button.copy-btn(type='button' :class='{ done: copiedId === "agent-" + ag.id }' @click='copyText(ag.id, "agent-" + ag.id)')
                      v-icon(size='14') mdi-content-copy
                      span {{copiedId === 'agent-' + ag.id ? $t('INTEGRATION.COMMON.COPIED') : $t('INTEGRATION.COMMON.COPY')}}
        .int-drawer-foot
          v-btn.int-btn-secondary(outlined @click='backToApiList') {{$t('INTEGRATION.REF.BACK')}}
      v-card.int-drawer-card(v-else)
        .int-drawer-head
          h2 {{$t('INTEGRATION.KEY.SET_ALLOWED_APIS')}}
          button.icon-btn(type='button' :aria-label='$t("INTEGRATION.COMMON.CANCEL")' :disabled='allowedSaving' @click='closeAllowed')
            v-icon(size='20') mdi-close
        .int-drawer-body
          dl.kv
            dt {{$t('INTEGRATION.LOG.COL_KEY')}}
            dd
              strong.mr-2 {{allowedTarget.name}}
              span.mono.faint {{allowedTarget.masked_key}}
          .helper.mb-3 {{$t('INTEGRATION.ALLOWED.DESCRIPTION')}}
          v-progress-linear(v-if='allowedLoading' indeterminate color='#32c7dc')
          .check-row(v-for='a in allowedApis' :key='a.api_code')
            v-checkbox.mt-0.pt-0(v-model='a.allowed' hide-details color='#32c7dc' :disabled='allowedSaving')
            div
              .t {{$t('INTEGRATION.API.' + a.api_code + '.NAME')}}
              .d {{$t('INTEGRATION.API.' + a.api_code + '.DESCRIPTION')}}
              button.btn-link(type='button' @click='viewApiReference(a.api_code)') {{$t('INTEGRATION.ALLOWED.VIEW_API_REFERENCE')}}
        .int-drawer-foot
          v-btn.int-btn-secondary(outlined :disabled='allowedSaving' @click='closeAllowed') {{$t('INTEGRATION.COMMON.CANCEL')}}
          v-btn.int-btn-primary(depressed :loading='allowedSaving' :disabled='allowedLoading' @click='saveAllowed') {{$t('INTEGRATION.COMMON.SAVE')}}
    v-dialog(v-model='logDialog' max-width='640' scrollable content-class='int-drawer' transition='slide-x-reverse-transition' overlay-color='#000' overlay-opacity='0.1')
      v-card.int-drawer-card
        .int-drawer-head
          h2 {{$t('INTEGRATION.LOG.DETAIL_TITLE')}}
          button.icon-btn(type='button' :aria-label='$t("INTEGRATION.COMMON.CANCEL")' @click='logDialog = false')
            v-icon(size='20') mdi-close
        .int-drawer-body
          v-progress-linear(v-if='logDetailLoading' indeterminate color='#32c7dc')
          template(v-else)
            dl.kv
              dt {{$t('INTEGRATION.LOG.COL_TIME')}}
              dd {{formatDate(logDetail.called_at)}}
              dt {{$t('INTEGRATION.LOG.COL_KEY')}}
              dd
                span.mr-2 {{logDetail.api_key_name}}
                span.mono.faint {{logDetail.masked_key}}
              dt {{$t('INTEGRATION.LOG.COL_API')}}
              dd {{apiName(logDetail.api_code)}}
              dt {{$t('INTEGRATION.LOG.COL_HTTP_STATUS')}}
              dd
                span.badge(:class='isSuccess(logDetail.http_status) ? "badge--green" : "badge--red"') {{logDetail.http_status}} {{isSuccess(logDetail.http_status) ? $t('INTEGRATION.LOG.SUCCESS') : $t('INTEGRATION.LOG.FAILED')}}
            .doc-h {{$t('INTEGRATION.REF.REQUEST_BODY')}}
            +codeBlock("formatBody(logDetail.request_body)", "'JSON'", "log-request")
            .doc-h {{$t('INTEGRATION.REF.RESPONSE')}}
            +codeBlock("formatBody(logDetail.response_body)", "'JSON'", "log-response")
    v-snackbar(v-model='snackbar' :timeout='3000' :color='snackbarColor') {{snackbarText}}
</template>

<script>
  import IntegrationService, { integration_full_url } from '@/services/IntegrationService';

  const TABS = ['api-keys', 'call-logs', 'how-to'];
  // api_code values of the integration APIs (names come from INTEGRATION.API.<code>.NAME)
  const API_CODES = ['simple_agent_ask'];
  // FRD section 10 errors (status and code are program values)
  const ERROR_ROWS = [
    { status: 401, code: 'missing_api_key' },
    { status: 401, code: 'invalid_api_key' },
    { status: 401, code: 'api_key_disabled' },
    { status: 403, code: 'api_not_allowed' },
    { status: 400, code: 'invalid_request' },
    { status: 404, code: 'agent_not_found' },
    { status: 504, code: 'agent_timeout' },
    { status: 500, code: 'internal_error' },
  ];

  export default {
    name: 'Integrations',
    data() {
      return {
        tab: this.tabFromQuery(this.$route.query.tab),
        headerName: 'X-API-Key',
        apiKeyPlaceholder: '<YOUR_API_KEY>',
        keys: [],
        loading: false,
        createDialog: false,
        createName: '',
        NAME_MAX_LENGTH: 50,
        nameError: '',
        submitting: false,
        keyDialog: false,
        createdKey: { name: '', key: '' },
        copied: false,
        snackbar: false,
        snackbarText: '',
        snackbarColor: 'error',
        busyId: null,
        deleteDialog: false,
        deleteTarget: { id: null, name: '' },
        deleting: false,
        allowedDialog: false,
        allowedTarget: { id: null, name: '', masked_key: '' },
        allowedApis: [],
        allowedLoading: false,
        allowedSaving: false,
        apiReferenceCode: '',
        agents: [],
        agentsLoading: false,
        agentsLoaded: false,
        agentsSeq: 0,
        copiedId: '',
        copiedTimer: null,
        logs: [],
        logsLoading: false,
        logPage: 1,
        logCount: 0,
        // Unfiltered total of call logs, shown in the tab label; null until an unfiltered list has been loaded
        logTotal: null,
        logPageSize: 50,
        logSeq: 0,
        logFilter: { api_key: this.$route.query.api_key || '', api_code: '', result: '', start: '', end: '' },
        logDialog: false,
        logDetail: {},
        logDetailLoading: false,
      };
    },
    computed: {
      createNameLength() { return (this.createName || "").trim().length; },
      keyOptions() {
        return [{ text: this.$t('INTEGRATION.LOG.FILTER_ALL'), value: '' }]
          .concat(this.keys.map((k) => ({ text: k.name, value: k.id })));
      },
      apiOptions() {
        return [{ text: this.$t('INTEGRATION.LOG.FILTER_ALL'), value: '' }]
          .concat(API_CODES.map((c) => ({ text: this.$t('INTEGRATION.API.' + c + '.NAME'), value: c })));
      },
      resultOptions() {
        return [
          { text: this.$t('INTEGRATION.LOG.FILTER_ALL'), value: '' },
          { text: this.$t('INTEGRATION.LOG.SUCCESS'), value: 'success' },
          { text: this.$t('INTEGRATION.LOG.FAILED'), value: 'failed' },
        ];
      },
      apiRef() {
        const a = this.allowedApis.find((x) => x.api_code === this.apiReferenceCode) || {};
        return { method: a.method || '', url: integration_full_url(a.path) };
      },
      headerLine() {
        return `${this.headerName}: ${this.apiKeyPlaceholder}`;
      },
      requestFields() {
        return [
          { name: 'agent_id', type: 'string' },
          { name: 'question', type: 'string' },
        ];
      },
      responseFields() {
        return [
          { name: 'answer', type: 'string' },
          { name: 'citations', type: 'object[]' },
          { name: 'index', type: 'integer', indent: true },
          { name: 'source', type: 'string', indent: true },
          { name: 'title', type: 'string', indent: true },
          { name: 'section', type: 'string|null', indent: true },
          { name: 'pages', type: 'integer[]', indent: true },
          { name: 'url', type: 'string|null', indent: true },
        ];
      },
      errorRows() {
        return ERROR_ROWS;
      },
      requestExample() {
        return JSON.stringify({ agent_id: '<AGENT_ID>', question: 'What is the return policy?' }, null, 2);
      },
      curlExample() {
        const body = JSON.stringify({ agent_id: '<AGENT_ID>', question: 'What is the return policy?' });
        return `curl -X ${this.apiRef.method} "${this.apiRef.url}" \\\n`
          + `  -H "${this.headerLine}" \\\n`
          + '  -H "Content-Type: application/json" \\\n'
          + `  -d '${body}'`;
      },
      responseExample() {
        return JSON.stringify({
          answer: 'Items can be returned within 30 days [1]. See also the shipping guide [2].',
          citations: [
            { index: 1, source: 'knowledge', title: 'return-policy.pdf', section: 'Returns', pages: [2, 3], url: null },
            { index: 2, source: 'web', title: 'Shipping guide', section: null, pages: [], url: 'https://example.com/shipping' },
          ],
        }, null, 2);
      },
      errorExample() {
        return JSON.stringify({ code: 'invalid_api_key', message: 'Invalid API key.' }, null, 2);
      },
      hasLogFilter() {
        const f = this.logFilter;
        return !!(f.api_key || f.api_code || f.result || f.start || f.end);
      },
      logPageCount() {
        return Math.ceil(this.logCount / this.logPageSize);
      },
    },
    created() {
      this.loadKeys();
      this.loadLogs();
    },
    watch: {
      '$route.query.api_key'(val) {
        const v = val || '';
        if (v === this.logFilter.api_key) return;
        this.logFilter.api_key = v;
        this.logPage = 1;
        this.loadLogs();
      },
      '$route.query.tab'(val) {
        const t = this.tabFromQuery(val);
        if (t !== this.tab) this.tab = t;
      },
    },
    methods: {
      tabFromQuery(val) {
        return TABS.includes(val) ? val : 'api-keys';
      },
      onTabChange(val) {
        if (!TABS.includes(val) || this.$route.query.tab === val) return;
        this.$router.replace({ path: this.$route.path, query: { ...this.$route.query, tab: val } }).catch(() => {});
      },
      goTab(val) {
        this.tab = val;
      },
      showError() {
        this.snackbarText = this.$t('INTEGRATION.TOAST.ERROR');
        this.snackbarColor = 'error';
        this.snackbar = true;
      },
      showSuccess(text) {
        this.snackbarText = text;
        this.snackbarColor = 'success';
        this.snackbar = true;
      },
      formatDate(val) {
        if (!val) return '';
        const d = new Date(val);
        if (isNaN(d.getTime())) return val;
        const p = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
      },
      loadKeys() {
        this.loading = true;
        return IntegrationService.list_api_keys()
          .then((res) => { this.keys = Array.isArray(res.data) ? res.data : []; })
          .catch(() => { this.showError(); })
          .finally(() => { this.loading = false; });
      },
      setCreateName(val) {
        this.createName = val;
        if (this.$refs.nameField) this.$refs.nameField.lazyValue = val;
      },
      onNameInput(v) {
        this.nameError = '';
        let val = String(v == null ? '' : v).replace(/^\s+/, '');
        const trimmedEnd = val.replace(/\s+$/, '');
        if (trimmedEnd.length > this.NAME_MAX_LENGTH) val = trimmedEnd.slice(0, this.NAME_MAX_LENGTH);
        if (val !== v) this.setCreateName(val);
      },
      onNameBlur() {
        this.setCreateName((this.createName || '').trim());
      },
      openCreate() {
        this.createName = '';
        this.nameError = '';
        this.createDialog = true;
      },
      submitCreate() {
        if (this.submitting) return;
        const name = (this.createName || '').trim();
        if (!name) {
          this.nameError = this.$t('INTEGRATION.KEY.NAME_REQUIRED');
          return;
        }
        this.submitting = true;
        IntegrationService.create_api_key({ name })
          .then((res) => {
            this.createdKey = { name: res.data.name, key: res.data.key };
            this.copied = false;
            this.createDialog = false;
            this.keyDialog = true;
          })
          .catch((err) => {
            const msgs = err.response && err.response.status === 400 && err.response.data && err.response.data.name;
            const msg = Array.isArray(msgs) ? msgs[0] : '';
            if (msg === 'This name is already in use.') this.nameError = this.$t('INTEGRATION.KEY.NAME_DUPLICATE');
            else if (msg === 'Name is required.') this.nameError = this.$t('INTEGRATION.KEY.NAME_REQUIRED');
            else this.showError();
          })
          .finally(() => { this.submitting = false; });
      },
      toggleActive(k) {
        if (this.busyId !== null) return;
        const target = !k.is_active;
        this.busyId = k.id;
        IntegrationService.update_api_key(k.id, { is_active: target })
          .then(() => {
            const key = target ? 'INTEGRATION.TOAST.ENABLED' : 'INTEGRATION.TOAST.DISABLED';
            this.showSuccess(this.$t(key, { name: k.name }));
            return this.loadKeys();
          })
          .catch(() => { this.showError(); })
          .finally(() => { this.busyId = null; });
      },
      openDelete(k) {
        this.deleteTarget = { id: k.id, name: k.name };
        this.deleteDialog = true;
      },
      confirmDelete() {
        if (this.deleting) return;
        this.deleting = true;
        IntegrationService.delete_api_key(this.deleteTarget.id)
          .then(() => {
            this.deleteDialog = false;
            // The key's call logs are deleted with it, so the last known total is no longer valid
            this.logTotal = null;
            this.showSuccess(this.$t('INTEGRATION.TOAST.DELETED'));
            return this.loadKeys();
          })
          .catch(() => {
            this.deleteDialog = false;
            this.showError();
          })
          .finally(() => { this.deleting = false; });
      },
      openAllowed(k) {
        this.allowedTarget = { id: k.id, name: k.name, masked_key: k.masked_key };
        this.allowedApis = [];
        this.apiReferenceCode = '';
        this.allowedLoading = true;
        this.allowedDialog = true;
        const id = k.id;
        IntegrationService.get_api_key_apis(id)
          .then((res) => {
            if (this.allowedTarget.id !== id) return;
            const apis = res.data && Array.isArray(res.data.apis) ? res.data.apis : [];
            this.allowedApis = apis.map((a) => ({ ...a, allowed: !!a.allowed }));
          })
          .catch(() => {
            this.allowedDialog = false;
            this.showError();
          })
          .finally(() => { this.allowedLoading = false; });
      },
      closeAllowed() {
        this.allowedDialog = false;
      },
      saveAllowed() {
        if (this.allowedSaving || this.allowedLoading) return;
        this.allowedSaving = true;
        const api_codes = this.allowedApis.filter((a) => a.allowed).map((a) => a.api_code);
        IntegrationService.save_api_key_apis(this.allowedTarget.id, { api_codes })
          .then(() => {
            this.allowedDialog = false;
            this.showSuccess(this.$t('INTEGRATION.TOAST.ALLOWED_SAVED'));
            return this.loadKeys();
          })
          .catch(() => { this.showError(); })
          .finally(() => { this.allowedSaving = false; });
      },
      viewCallLogs(k) {
        this.$router.push({ path: this.$route.path, query: { ...this.$route.query, tab: 'call-logs', api_key: k.id } }).catch(() => {});
      },
      isSuccess(status) {
        return status >= 200 && status < 300;
      },
      apiName(code) {
        return code ? this.$t('INTEGRATION.API.' + code + '.NAME') : '';
      },
      formatBody(val) {
        if (val === null || val === undefined || val === '') return '';
        if (typeof val !== 'string') return JSON.stringify(val, null, 2);
        try {
          return JSON.stringify(JSON.parse(val), null, 2);
        } catch (e) {
          return val;
        }
      },
      syncApiKeyQuery() {
        const q = { ...this.$route.query };
        if (this.logFilter.api_key) q.api_key = this.logFilter.api_key;
        else delete q.api_key;
        if ((q.api_key || '') === (this.$route.query.api_key || '')) return;
        this.$router.replace({ path: this.$route.path, query: q }).catch(() => {});
      },
      onFilterChange() {
        this.syncApiKeyQuery();
        this.logPage = 1;
        this.loadLogs();
      },
      clearLogFilter() {
        this.logFilter = { api_key: '', api_code: '', result: '', start: '', end: '' };
        this.onFilterChange();
      },
      loadLogs() {
        const params = { page: this.logPage };
        ['api_key', 'api_code', 'result', 'start', 'end'].forEach((f) => {
          if (this.logFilter[f]) params[f] = this.logFilter[f];
        });
        const unfiltered = !this.hasLogFilter;
        const seq = ++this.logSeq;
        this.logsLoading = true;
        return IntegrationService.list_call_logs(params)
          .then((res) => {
            if (seq !== this.logSeq) return;
            const d = res.data || {};
            this.logs = Array.isArray(d.results) ? d.results : [];
            this.logCount = d.count || 0;
            if (unfiltered) this.logTotal = this.logCount;
            if (d.page_size) this.logPageSize = d.page_size;
          })
          .catch(() => {
            if (seq !== this.logSeq) return;
            this.logs = [];
            this.logCount = 0;
            this.showError();
          })
          .finally(() => { if (seq === this.logSeq) this.logsLoading = false; });
      },
      openLogDetail(l) {
        const id = l.id;
        this.logDetail = { ...l };
        this.logDetailLoading = true;
        this.copiedId = '';
        this.logDialog = true;
        IntegrationService.get_call_log(id)
          .then((res) => {
            if (this.logDetail.id !== id) return;
            this.logDetail = res.data || {};
          })
          .catch(() => {
            this.logDialog = false;
            this.showError();
          })
          .finally(() => { if (this.logDetail.id === id) this.logDetailLoading = false; });
      },
      viewApiReference(apiCode) {
        this.apiReferenceCode = apiCode;
        this.copiedId = '';
        this.loadAgents();
      },
      backToApiList() {
        this.apiReferenceCode = '';
        this.copiedId = '';
      },
      loadAgents() {
        const seq = ++this.agentsSeq;
        this.agentsLoading = true;
        this.agentsLoaded = false;
        this.agents = [];
        return IntegrationService.list_agents()
          .then((res) => {
            if (seq !== this.agentsSeq) return;
            this.agents = Array.isArray(res.data) ? res.data : [];
            this.agentsLoaded = true;
          })
          .catch(() => {
            if (seq !== this.agentsSeq) return;
            this.showError();
          })
          .finally(() => { if (seq === this.agentsSeq) this.agentsLoading = false; });
      },
      copyText(text, id) {
        const done = () => {
          this.copiedId = id;
          clearTimeout(this.copiedTimer);
          this.copiedTimer = setTimeout(() => { this.copiedId = ''; }, 2000);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).then(done).catch(() => this.showError());
        } else {
          const ta = document.createElement('textarea');
          ta.value = text;
          document.body.appendChild(ta);
          ta.select();
          try { document.execCommand('copy'); done(); } catch (e) { this.showError(); }
          document.body.removeChild(ta);
        }
      },
      copyKey() {
        const key = this.createdKey.key;
        const done = () => { this.copied = true; };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(key).then(done).catch(() => this.showError());
        } else {
          const ta = document.createElement('textarea');
          ta.value = key;
          document.body.appendChild(ta);
          ta.select();
          try { document.execCommand('copy'); done(); } catch (e) { this.showError(); }
          document.body.removeChild(ta);
        }
      },
      closeKeyDialog() {
        this.keyDialog = false;
        this.createdKey = { name: '', key: '' };
        this.copied = false;
        this.loadKeys();
      },
    },
  };
</script>

<!-- Containers created by v-dialog (teleported outside this component's root) -->
<style>
.int-dialog-wrap .v-dialog,
.int-drawer .v-dialog {
  border-radius: 8px;
  box-shadow: 0 0 20px rgba(0, 0, 0, .2); /* taste-skill-ignore: overlay */
}
.int-drawer.v-dialog__content {
  justify-content: flex-end;
  align-items: stretch;
}
.int-drawer .v-dialog {
  margin: 0;
  max-height: 100%;
  height: 100%;
  width: 100%;
  border-radius: 0;
  box-shadow: -4px 0 16px rgba(0, 0, 0, .14); /* taste-skill-ignore: overlay */
}
</style>

<style scoped>
/* Values follow the design prototype (proto2.html) */
#Integrations {
  font-family: 'Segoe UI', 'Noto Sans TC', 'Microsoft JhengHei', sans-serif;
  letter-spacing: .3px;
  color: #333;
}
.int-head.row {
  flex: 0 0 auto;
  margin: 0;
  padding-top: 13px;
  min-height: 40px;
}
.int-head h1 {
  letter-spacing: .5px;
  color: #1f2020;
}
.page-sub {
  font-size: 14px;
  color: #666;
}
.int-inner {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  padding: 0 8px 32px;
}
.faint {
  color: #8a8f93;
}
.nowrap {
  white-space: nowrap;
}
.mono {
  font-family: 'Cascadia Code', 'Consolas', 'SFMono-Regular', Menlo, monospace;
  font-size: 13px;
  letter-spacing: 0;
}

/* Panel and pill tabs (.dpanel / .dbar / .dseg) */
.dpanel {
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 1px 5px rgba(0, 0, 0, .12);
  margin-top: 24px;
}
.dbar {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 16px 6px;
  flex-wrap: wrap;
}
.dbar-right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 16px;
}
.int-seg.v-tabs {
  flex: 0 0 auto;
  width: fit-content;
}
.int-seg >>> .v-tabs-bar {
  height: auto !important;
  background: transparent !important;
}
.int-seg >>> .v-tabs-bar__content {
  gap: 13px;
}
.int-seg >>> .v-tab {
  height: 32px;
  min-width: 0;
  padding: 0 12px;
  border: 1px solid #ececec;
  border-radius: 16px;
  font-size: 15px;
  font-weight: 400;
  letter-spacing: .3px;
  text-transform: none;
  background: #f4f4f4;
  color: #666 !important;
  transition: background .15s ease-out;
}
.int-seg >>> .v-tab::before {
  display: none;
}
.int-seg >>> .v-tab:hover {
  background: #ececec;
}
.int-seg >>> .v-tab--active {
  background: #e1effd;
  border-color: #2f73c3;
  color: #2f73c3 !important;
}

/* Buttons (.btn) */
.int-btn-primary.v-btn,
.int-btn-secondary.v-btn,
.int-btn-danger.v-btn {
  height: 40px;
  padding: 0 16px;
  border-radius: 4px;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: .3px;
  text-transform: none;
}
.plus-ic {
  flex: none;
  margin-right: 6px;
}
.int-btn-primary.v-btn:not(.v-btn--disabled) {
  background-color: #32c7dc !important;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
}
.int-btn-primary.v-btn:not(.v-btn--disabled):hover {
  background-color: #22b6cc !important;
}
.int-btn-primary.v-btn:not(.v-btn--disabled):active {
  background-color: #1aa3b8 !important;
}
.int-btn-primary.v-btn.v-btn--disabled {
  background-color: #ccc !important;
  color: #fff !important;
}
.int-btn-secondary.v-btn:not(.v-btn--disabled) {
  border: 1px solid #888;
  background: #fff;
  color: #444;
  font-weight: 500;
}
.int-btn-secondary.v-btn:not(.v-btn--disabled):hover {
  background: #f4f4f4;
}
.int-btn-secondary.v-btn:not(.v-btn--disabled):active {
  background: #e8e8e8;
}
.int-btn-danger.v-btn:not(.v-btn--disabled) {
  background-color: #d93025 !important;
  color: #fff;
}
.int-btn-danger.v-btn:not(.v-btn--disabled):hover {
  background-color: #b3261e !important;
}
.int-btn-sm.v-btn {
  height: 32px;
  padding: 0 12px;
  font-size: 14px;
}
.btn-link {
  border: none;
  background: none;
  padding: 0;
  color: #14C6DE;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: color .15s ease;
}
.btn-link:hover {
  color: #07B2D3;
}
.btn-link:active {
  color: #1095AF;
}
.btn-link:focus-visible {
  outline: 2px solid #14C6DE;
  outline-offset: 2px;
  border-radius: 2px;
}
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}
.how-to-link {
  display: inline-block;
  line-height: 1.5;
  color: #14C6DE;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: color .15s ease;
}
.how-to-link:hover {
  color: #07B2D3;
}
.how-to-link:active {
  color: #1095AF;
}
.icon-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #555;
  flex: none;
}
.icon-btn .v-icon {
  color: inherit;
}
.icon-btn:hover {
  background: #f0f0f0;
}
.icon-btn:focus-visible {
  outline: 2px solid #14C6DE;
  outline-offset: 1px;
}
.icon-btn:disabled {
  opacity: .35;
  cursor: not-allowed;
}
.icon-btn:disabled:hover {
  background: transparent;
}
.copy-btn {
  height: 28px;
  padding: 0 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #fff;
  font-size: 12px;
  letter-spacing: .3px;
  color: #333;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: none;
}
.copy-btn:hover {
  background: #f6f6f6;
}
.copy-btn.done {
  border-color: #1e7e34;
  color: #1e7e34;
}

/* Table (.dwrap / .dtable) */
.dwrap {
  padding: 0 16px;
  overflow-x: auto;
}
.integration-api-keys .dwrap {
  padding-bottom: 12px;
}
.dtable {
  width: 100%;
  border-collapse: collapse;
  font-size: 15px;
  letter-spacing: .3px;
}
.dtable th {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  background: #fff;
  padding: 14px 20px;
  text-align: left;
  white-space: nowrap;
}
.dtable td {
  padding: 10px 20px;
  border-bottom: 1px solid #e0e0e0;
  height: 63px;
  color: #333;
  vertical-align: middle;
}
.dtable tr.muted td {
  color: #9a9a9a;
}
.dtable tr.clickable {
  cursor: pointer;
}
.dtable tr.clickable:hover td {
  background: #f2fafc;
}
.dtable .sub {
  font-size: 12px;
  color: #8a8f93;
  margin-top: 2px;
}
.cell-name {
  font-weight: 400;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ex {
  display: flex;
  align-items: center;
  gap: 2px;
}
.badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  padding: 0 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: .3px;
  white-space: nowrap;
}
.badge .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}
.badge.badge--green {
  background: #e6f4ea;
  color: #1e7e34;
}
.badge.badge--grey {
  background: #efefef;
  color: #666;
}
.badge.badge--red {
  background: #fdecea;
  color: #c62828;
}

/* Empty state (.empty) */
.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
  padding: 56px 24px;
  color: #666;
  font-size: 15px;
}
.empty .ill {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #f2fafc;
  display: flex;
  align-items: center;
  justify-content: center;
}
.empty p {
  margin: 0;
  max-width: 420px;
  line-height: 1.6;
}

/* Call log filters (.dfilters) and pager (.dpager) */
.dfilters {
  display: flex;
  gap: 16px;
  padding: 8px 16px 12px;
  flex-wrap: wrap;
  align-items: flex-end;
  border-bottom: 1px solid #e0e0e0;
  margin: 0 16px;
}
.dfilters .f {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.ds-label {
  font-size: 12px;
  font-weight: 600;
  color: #444;
  letter-spacing: .3px;
}
.log-filter {
  width: 160px;
  min-width: 160px;
  flex: none;
  font-size: 12px;
  letter-spacing: .3px;
  margin: 0;
  padding: 0;
}
.log-filter >>> .v-input__slot {
  min-height: 32px !important;
  height: 32px;
  padding: 0 10px !important;
  margin: 0;
}
.log-filter >>> fieldset {
  border: 1px solid #ccc;
  border-radius: 4px;
}
.log-filter >>> .v-input__slot:hover fieldset {
  border-color: #888;
}
.log-filter.v-input--is-focused >>> fieldset {
  border: 1px solid #14C6DE;
}
.log-filter >>> .v-text-field__slot input,
.log-filter >>> .v-select__selection {
  font-size: 12px;
  color: #222;
  padding: 0;
  margin: 0;
}
.log-filter >>> .v-input__append-inner {
  margin-top: 4px !important;
  padding-left: 0;
}
.log-filter >>> .v-input__icon .v-icon {
  font-size: 18px;
  color: #999;
}
.log-filter >>> .v-select__selections {
  flex-wrap: nowrap;
}
.dpager {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  padding: 14px 0 18px;
}
.dpager .total {
  font-size: 13px;
  color: #8a8f93;
  margin-right: 8px;
}
.dpager >>> .v-pagination {
  width: auto;
}
.dpager >>> .v-pagination__item {
  width: 34px;
  height: 34px;
  min-width: 34px;
  margin: 0 5px;
  font-size: 14px;
  color: #333;
  background: transparent !important;
  box-shadow: none;
}
.dpager >>> .v-pagination__item:hover {
  background: #f2f2f2 !important;
}
.dpager >>> .v-pagination__item--active {
  background: #4a8cf0 !important;
  color: #fff;
  box-shadow: 0 2px 4px rgba(74, 140, 240, .4);
}
.dpager >>> .v-pagination__navigation {
  width: 36px;
  height: 36px;
  background: none !important;
  box-shadow: none;
  color: #777;
}

/* How-to steps (.steps / .step) */
.steps {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
}
.step {
  display: flex;
  gap: 16px;
  border: 1px solid #ececec;
  border-radius: 8px;
  padding: 20px;
}
.step-no {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #32c7dc;
  color: #fff;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
}
.step h3 {
  margin: 4px 0 6px;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
  color: #1f2020;
}
.step p {
  margin: 0 0 10px !important;
  font-size: 14px;
  line-height: 1.7;
  color: #666;
}
.step-main {
  min-width: 0;
  flex: 1;
}
.step .note {
  font-size: 13px;
  line-height: 1.5;
  color: #8a8f93;
  margin-top: 10px;
}

/* Code blocks (.code) */
.code-block {
  border: 1px solid #e3e6ea;
  border-radius: 6px;
  background: #f7f9fb;
  overflow: hidden;
  margin-top: 8px;
}
.code-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 6px 4px 12px;
  border-bottom: 1px solid #e3e6ea;
  font-size: 12px;
  color: #666;
  background: #f0f3f6;
}
.code-block pre {
  margin: 0;
  padding: 12px 14px;
  font-family: 'Cascadia Code', 'Consolas', 'SFMono-Regular', Menlo, monospace;
  font-size: 12.5px;
  line-height: 1.6;
  letter-spacing: 0;
  white-space: pre-wrap;
  word-break: break-all;
  color: #24292f;
  max-height: 360px;
  overflow: auto;
}

/* Dialogs (.dialog) */
.int-dialog {
  display: flex;
  flex-direction: column;
  min-height: 240px;
  max-height: 90vh;
  border-radius: 8px;
  overflow: hidden;
  font-family: 'Segoe UI', 'Noto Sans TC', 'Microsoft JhengHei', sans-serif;
  letter-spacing: .3px;
}
.int-dhead {
  flex: none;
  height: 60px;
  display: flex;
  align-items: center;
  padding: 0 24px;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
}
.int-dhead h2 {
  margin: 0;
  font-weight: 700;
  font-size: 20px;
  color: #222;
  letter-spacing: .5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.int-dbody {
  flex: 1;
  overflow: auto;
  padding: 20px 24px;
  font-size: 14px;
  line-height: 1.7;
  color: #444;
  letter-spacing: .5px;
}
.int-dactions {
  flex: none;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  padding: 0 24px;
  background: #f4f4f4;
}
.int-dactions .v-btn.v-btn {
  min-width: 100px;
  height: 40px;
  padding: 0 16px;
  font-size: 16px;
  font-weight: 500;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 18px;
}
.field-label-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}
.field-counter {
  font-size: 12px;
  color: #888;
}
.field-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #333;
}
.req {
  color: #d93025;
  margin-left: 2px;
}
.field >>> .v-input__slot {
  min-height: 38px !important;
  padding: 0 12px !important;
  margin-bottom: 6px;
}
.field >>> fieldset {
  border: 1px solid #ccc;
  border-radius: 4px;
}
.field >>> .v-text-field__slot input {
  font-size: 14px;
  color: #333;
  padding: 8px 0;
}
.field >>> .v-input--is-focused .v-input__slot {
  background: #f4fcfd;
}
.field >>> .v-input--is-focused fieldset {
  border: 1px solid #32c7dc;
}
.field >>> .v-input.error--text fieldset {
  border: 1px solid #d93025;
}
.field >>> .v-messages {
  font-size: 12px;
  color: #8a8f93;
}
.field >>> .v-input.error--text .v-messages,
.field >>> .v-messages.error--text {
  color: #d93025;
}
.key-box {
  display: flex;
  gap: 8px;
  align-items: center;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 6px 6px 6px 12px;
  margin: 16px 0;
  background: #f7f9fb;
}
.key-box code {
  flex: 1;
  font-family: 'Cascadia Code', 'Consolas', 'SFMono-Regular', Menlo, monospace;
  font-size: 13px;
  word-break: break-all;
  letter-spacing: 0;
  color: #1f2020;
  background: transparent;
  box-shadow: none;
  padding: 0;
}
.callout {
  display: flex;
  gap: 10px;
  border-radius: 6px;
  padding: 12px 14px;
  font-size: 14px;
  line-height: 1.6;
  letter-spacing: .3px;
}
.callout + .callout {
  margin-top: 10px;
}
.callout .v-icon {
  flex: none;
  align-self: flex-start;
  margin-top: 2px;
}
.callout.callout--warn {
  background: #fff6e0;
  border: 1px solid #f2c45a;
  color: #8a5a00;
}
.callout.callout--info {
  background: #f2fafc;
  border: 1px solid #bfe9f1;
  color: #20606b;
}

/* Drawer (.drawer) */
.int-drawer-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 0;
  font-family: 'Segoe UI', 'Noto Sans TC', 'Microsoft JhengHei', sans-serif;
  letter-spacing: .3px;
}
.int-drawer-head {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 60px;
  padding: 0 12px 0 24px;
  border-bottom: 1px solid #ececec;
  flex: none;
}
.int-drawer-head h2 {
  margin: 0;
  font-size: 18px;
  flex: 1;
  color: #1f2020;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.int-drawer-body {
  flex: 1;
  overflow: auto;
  padding: 20px 24px;
  font-size: 14px;
  color: #444;
}
.int-drawer-foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 24px;
  border-top: 1px solid #ececec;
  flex: none;
}
.kv {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 8px 16px;
  font-size: 14px;
  margin-bottom: 8px;
}
.kv dt {
  color: #666;
}
.kv dd {
  margin: 0;
}
.helper {
  font-size: 12px;
  color: #8a8f93;
  line-height: 1.5;
}
.check-row {
  display: flex;
  gap: 4px;
  align-items: flex-start;
  border: 1px solid #ececec;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 10px;
}
.check-row .t {
  font-weight: 600;
  font-size: 15px;
}
.check-row .d {
  font-size: 13px;
  color: #666;
  margin: 3px 0 8px;
  line-height: 1.6;
}
.doc-h {
  font-size: 14px;
  font-weight: 700;
  color: #1f2020;
  margin: 22px 0 4px;
}
.doc-h.first {
  margin-top: 4px;
}
.mini-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin-top: 8px;
  border: 1px solid #ececec;
  border-radius: 6px;
}
.mini-table th,
.mini-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #ececec;
  text-align: left;
  vertical-align: top;
}
.mini-table th {
  background: #fafbfc;
  font-size: 12px;
}
.ref-indent {
  padding-left: 16px;
}
.inline-code {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.inline-code code {
  font-family: 'Cascadia Code', 'Consolas', 'SFMono-Regular', Menlo, monospace;
  font-size: 13px;
  background: #f0f3f6;
  border: 1px solid #e3e6ea;
  border-radius: 4px;
  padding: 3px 8px;
  letter-spacing: 0;
  box-shadow: none;
  color: #24292f;
  word-break: break-all;
}
.method {
  display: inline-block;
  font-family: 'Cascadia Code', 'Consolas', 'SFMono-Regular', Menlo, monospace;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  background: #1f7a3a;
  border-radius: 4px;
  padding: 2px 8px;
  letter-spacing: 0;
}
</style>
